"use client";

import React from "react";
import { ArrowUpRight, Info } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
interface ProjectItem {
  id: string;
  category: string;
  title: string;
  caption: string;
  imageSrc: string;
  imageSrcSet: string;
  imageSizes: string;
  imageAlt: string;
}
const projects: ProjectItem[] = [{
  id: "project-1",
  category: "OPEN LAYOUT",
  title: "Transitional Open Living & Dining Pavilion",
  caption: "Removed primary bearing partitions to create a continuous daylight corridor unified by white oak ceiling beams.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_living_pavilion.png",
  imageSrcSet: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_living_pavilion.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_living_pavilion.png-wvc-sizes",
  imageAlt: "Open-concept Brookhaven living room with custom white oak ceiling beams and wide plank floors"
}, {
  id: "project-2",
  category: "ARCHITECTURAL MILLWORK",
  title: "Tailored Architectural Chef's Scullery",
  caption: "Integrated custom reeded cabinetry, a monolithic honed quartzite prep island, and aged brass fittings.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_scullery_kitchen.png",
  imageSrcSet: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_scullery_kitchen.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_scullery_kitchen.png-wvc-sizes",
  imageAlt: "Chef kitchen remodel with reeded white oak cabinetry and honed quartzite island"
}, {
  id: "project-3",
  category: "INTERIOR TRANSFORMATION",
  title: "Grand Architectural Entry Foyer",
  caption: "Installed bespoke perimeter wainscoting, solid white oak stair treads, and hand-turned iron balusters.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_foyer_staircase.png",
  imageSrcSet: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_foyer_staircase.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_foyer_staircase.png-wvc-sizes",
  imageAlt: "Two-story grand entry foyer with custom wall paneling and architectural iron staircase"
}, {
  id: "project-4",
  category: "BESPOKE FINISHES",
  title: "Bespoke Master Suite Retreat",
  caption: "Anchored the primary suite with a fluted limestone fireplace hearth and floor-to-ceiling library millwork.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_master_suite.png",
  imageSrcSet: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_master_suite.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_master_suite.png-wvc-sizes",
  imageAlt: "Master bedroom suite with fluted limestone fireplace and integrated oak built-in shelves"
}];
export default function Gallery() {
  const shouldReduceMotion = useReducedMotion();
  const easeCurve: [number, number, number, number] = [0.16, 1, 0.3, 1];
  const headerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 15
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: easeCurve
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 15
    },
    visible: (index: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        delay: shouldReduceMotion ? 0 : index * 0.08,
        ease: easeCurve
      }
    })
  };
  return <section id="project-gallery" data-nav="light" className="bg-background text-foreground py-20 md:py-24">
      <div className="container mx-auto px-5 sm:px-8 lg:px-16 max-w-[1440px]">
        {/* Header Introduction */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={headerVariants} className="max-w-3xl mb-12 md:mb-16">
          <Badge variant="outline" className="mb-4 rounded-full border-primary/40 bg-primary/10 px-3.5 py-1 text-xs font-semibold tracking-widest text-foreground uppercase">
            Project Gallery
          </Badge>

          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-[1.2]">
            Whole-home transformations in Brookhaven
          </h2>

          <div className="my-5 w-24 h-[1px] bg-primary/60" />

          <p className="font-sans text-base sm:text-lg text-foreground/80 leading-relaxed max-w-2xl">
            Explore how we reconfigure interior flow, expand architectural daylight,
            and install tailored millwork across established Brookhaven neighborhoods.
          </p>
        </motion.div>

        {/* Remodeling Inspiration & Design Concepts Disclaimer */}
        <div className="mb-8 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3.5 sm:px-5 sm:py-4 flex items-start sm:items-center gap-3 text-xs sm:text-sm text-foreground/85">
          <Info className="size-4 shrink-0 text-primary mt-0.5 sm:mt-0" aria-hidden="true" />
          <p className="font-sans leading-relaxed">
            <span className="font-semibold text-foreground">Design Note:</span> These images are Remodeling Inspiration &amp; Design Concepts — they are not photographs of completed Brookhaven Elevation Renovations projects.
          </p>
        </div>

        {/* 2x2 Gallery Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
          {projects.map((project, index) => <motion.div key={project.id} data-index={index} custom={index} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.15
        }} variants={cardVariants} className="h-full">
              <Card className="group h-full border border-border/70 bg-card rounded-xl overflow-hidden shadow-xs hover:shadow-md hover:border-primary/50 transition-[border-color,box-shadow,transform] duration-250 hover:-translate-y-1 p-0 gap-0">
                {/* 3:2 Architectural Photography Container */}
                <div className="relative aspect-[3/2] w-full overflow-hidden bg-muted">
                  <img src={project.imageSrc} data-wvc-srcset={project.imageSrcSet} data-wvc-sizes={project.imageSizes} alt={project.imageAlt} loading="lazy" decoding="async" className="h-full w-full object-cover object-center transition-transform duration-400 ease-out group-hover:scale-105" />
                  <div className="absolute inset-0 ring-1 ring-inset ring-foreground/5 pointer-events-none" />
                </div>

                {/* Metadata Panel */}
                <CardContent className="p-6 md:p-7 flex flex-col justify-between flex-1">
                  <div>
                    <span className="inline-block font-sans text-xs font-semibold tracking-wider text-muted-foreground uppercase mb-2">
                      {project.category}
                    </span>

                    <h3 className="font-serif text-xl sm:text-2xl font-semibold tracking-tight text-foreground leading-snug mb-3">
                      {project.title}
                    </h3>

                    <p className="font-sans text-sm sm:text-base text-foreground/75 leading-relaxed">
                      {project.caption}
                    </p>
                  </div>

                  <div className="pt-5 mt-5 border-t border-border/60 flex items-center justify-between">
                    <span className="font-sans text-xs text-muted-foreground font-medium">
                      Brookhaven, GA
                    </span>
                    <span className="inline-flex items-center text-xs font-semibold tracking-wide text-foreground group-hover:text-primary transition-colors">
                      View details
                      <ArrowUpRight className="ml-1 size-3.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </div>

        {/* Section Bottom CTA Area */}
        <Separator className="mt-16 md:mt-20 mb-10 bg-border/60" />

        <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="text-center sm:text-left">
            <h4 className="font-sans text-lg font-semibold text-foreground">
              Ready to reimagine your living space?
            </h4>
            <p className="font-sans text-sm text-foreground/75">
              Review our complete library of completed local home transformations.
            </p>
          </div>

          <Button asChild variant="secondary" className="w-full sm:w-auto px-7 py-3.5 h-auto text-base font-medium rounded-lg shadow-xs hover:shadow-md transition-[background-color,box-shadow,transform] duration-200 hover:-translate-y-0.5 shrink-0">
            <Link to="/OurProjectsPage">
              View Our Projects
              <ArrowUpRight className="ml-1 size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}